import type { BookingColorPair, BookingSection, BookingThemeSection, ProfileSection } from "./bookingTheme";
import { PROFILE_SECTION_FIELDS, ZBookingThemeSchema } from "./bookingTheme";
import { createColorMap, getWCAGContrastColor, isValidHexCode } from "./getBrandColours";

type CssVars = Record<string, string>;
type BookingColoursTheme = { light: CssVars; dark: CssVars };
type Mode = "light" | "dark";

/** Maps a logical section to its CSS variable prefix (matches tokens.css). */
const SECTION_VAR_PREFIX: Record<BookingThemeSection, string> = {
  slots: "cal-booking-slots",
  calendar: "cal-booking-calendar",
  confirmation: "cal-booking-confirm",
  success: "cal-booking-success",
};

/** Maps a profile background field to its CSS variable name (matches tokens.css). */
const PROFILE_VAR_NAME: Record<(typeof PROFILE_SECTION_FIELDS)[number], string> = {
  nameBg: "cal-profile-name-bg",
  cardBg: "cal-profile-card-bg",
  cardHover: "cal-profile-card-hover",
  durationBg: "cal-profile-duration-bg",
  pillActive: "cal-profile-pill-active",
};

/**
 * Fonds du profil dont la couleur de TEXTE est dérivée par contraste (et donc jamais exposée
 * au praticien : la lui laisser régler permettrait d'écrire illisible sur son propre fond).
 */
const PROFILE_DERIVED_TEXT: Partial<Record<(typeof PROFILE_SECTION_FIELDS)[number], string>> = {
  durationBg: "cal-profile-duration-text",
  pillActive: "cal-profile-pill-active-text",
};

/** Strip leading `#` and expand a 3-digit hex to 6 digits (createColorMap expects no `#`). */
function normalizeHex(hex: string): string {
  let value = hex.replace("#", "");
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return value;
}

/**
 * Builds the `--cal-booking-*` CSS variables for a single section/mode.
 * - accent  -> base colour (+ derived emphasis, + readable contrast text when no text override)
 * - text    -> explicit text override (the "key override" of the hybrid model)
 * Only emits variables that are actually customized; anything omitted falls back
 * to the brand colour via the CSS token defaults in tokens.css.
 */
function buildSectionVars(prefix: string, mode: Mode, section: BookingSection): CssVars {
  const vars: CssVars = {};
  const accent = section.accent?.[mode];
  const text = section.text?.[mode];

  if (accent && isValidHexCode(accent)) {
    const map = createColorMap(normalizeHex(accent));
    vars[prefix] = map["500"];
    vars[`${prefix}-emphasis`] = mode === "light" ? map["400"] : map["600"];
    // Calendar uses an "accent" colour for the active availability dot.
    if (prefix === SECTION_VAR_PREFIX.calendar) {
      vars[`${prefix}-accent`] = getWCAGContrastColor(map["500"]);
      // Jours DISPONIBLES : teinte claire (sombre en mode sombre) dérivée du MÊME accent, pour que
      // la grille reste cohérente. Non exposé au praticien : c'est une couleur dépendante.
      vars[`${prefix}-day`] = mode === "light" ? map["50"] : map["900"];
      vars[`${prefix}-day-text`] = mode === "light" ? map["700"] : map["300"];
    }
    // Confirmation/cancellation page colours its title with the accent base colour.
    if (prefix === SECTION_VAR_PREFIX.success) {
      vars[`${prefix}-title`] = map["500"];
    }
    // Default the section text to a readable contrast colour on the new accent.
    vars[`${prefix}-text`] = getWCAGContrastColor(map["500"]);
  }

  // Explicit text override wins over the derived contrast colour.
  if (text && isValidHexCode(text)) {
    vars[`${prefix}-text`] = text;
  }

  // Optional background colour (raw value, no derivation).
  const bg = section.bg?.[mode];
  if (bg && isValidHexCode(bg)) {
    vars[`${prefix}-bg`] = bg;
  }

  return vars;
}

/**
 * Turns a (possibly untrusted) `bookingTheme` value into a `{ light, dark }` map of
 * CSS variables, ready to feed to `useCalcomTheme`. Returns empty maps when nothing
 * is customized, leaving the brand fallbacks in place.
 */
export function getBookingColours(bookingTheme: unknown): BookingColoursTheme {
  const empty: BookingColoursTheme = { light: {}, dark: {} };
  if (!bookingTheme) return empty;

  const parsed = ZBookingThemeSchema.safeParse(bookingTheme);
  if (!parsed.success) return empty;

  const theme = parsed.data;
  const result: BookingColoursTheme = { light: {}, dark: {} };

  (Object.keys(SECTION_VAR_PREFIX) as BookingThemeSection[]).forEach((sectionName) => {
    const section = theme[sectionName];
    if (!section) return;
    const prefix = SECTION_VAR_PREFIX[sectionName];
    Object.assign(result.light, buildSectionVars(prefix, "light", section));
    Object.assign(result.dark, buildSectionVars(prefix, "dark", section));
  });

  if (theme.profile) {
    Object.assign(result.light, buildProfileVars("light", theme.profile));
    Object.assign(result.dark, buildProfileVars("dark", theme.profile));
  }

  // Overall booking widget background (raw value, no derivation).
  if (theme.background?.light && isValidHexCode(theme.background.light)) {
    result.light["cal-booking-bg"] = theme.background.light;
  }
  if (theme.background?.dark && isValidHexCode(theme.background.dark)) {
    result.dark["cal-booking-bg"] = theme.background.dark;
  }

  // Fond de PAGE des vitrines publiques (derrière la carte). Valeur brute, sans dérivation.
  if (theme.pageBg?.light && isValidHexCode(theme.pageBg.light)) {
    result.light["cal-public-page-bg"] = theme.pageBg.light;
  }
  if (theme.pageBg?.dark && isValidHexCode(theme.pageBg.dark)) {
    result.dark["cal-public-page-bg"] = theme.pageBg.dark;
  }

  return result;
}

/** Profile page colours are raw background values applied verbatim (no derivation). */
function buildProfileVars(mode: Mode, profile: ProfileSection): CssVars {
  const vars: CssVars = {};
  PROFILE_SECTION_FIELDS.forEach((field) => {
    const pair = profile[field] as BookingColorPair | undefined;
    const value = pair?.[mode];
    if (value && isValidHexCode(value)) {
      vars[PROFILE_VAR_NAME[field]] = value;
      // Texte dérivé par contraste sur les fonds qui en portent (pastille de durée, pilule active).
      const textVar = PROFILE_DERIVED_TEXT[field];
      if (textVar) {
        vars[textVar] = getWCAGContrastColor(value);
      }
    }
  });
  return vars;
}

export default getBookingColours;
